import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { BookOpen } from "lucide-react";

// `cosmic` prop used by Ella Raine page for cyan/violet accents
export default function BookCard({ book, index = 0, cosmic = false }) {
  const [imgFailed, setImgFailed] = useState(false);
  const accent = cosmic ? "#00E5FF" : "#D4AF37";
  const accentBg = cosmic ? "rgba(0, 229, 255, 0.1)" : "rgba(212, 175, 55, 0.1)";
  const borderCol = cosmic ? "rgba(0, 229, 255, 0.25)" : "#3A4757";
  const fallbackBg = cosmic
    ? "linear-gradient(135deg, #0A0B15 0%, #111222 60%, rgba(0,229,255,0.15) 100%)"
    : "linear-gradient(135deg, #1A1D24 0%, #2B3A4A 60%, rgba(212,175,55,0.15) 100%)";

  return (
    <motion.article
      data-testid={`book-card-${book.slug}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.7, delay: index * 0.08 }}
      className="group relative"
    >
      <Link to={`/books/${book.slug}`} className="block">
        <div
          className="overflow-hidden rounded-2xl backdrop-blur-xl"
          style={{
            backgroundColor: cosmic ? "rgba(17, 18, 34, 0.85)" : "rgba(43, 58, 74, 0.85)",
            border: `1px solid ${borderCol}`,
            boxShadow: cosmic
              ? "0 12px 40px rgba(0, 229, 255, 0.1)"
              : "0 12px 40px rgba(0, 0, 0, 0.5)",
          }}
        >
          <div className="relative aspect-[3/4] overflow-hidden">
            {imgFailed ? (
              <div
                className="absolute inset-0 flex flex-col items-center justify-center p-6 text-center"
                style={{ background: fallbackBg }}
                data-testid={`book-cover-fallback-${book.slug}`}
              >
                <BookOpen size={40} style={{ color: accent, opacity: 0.6 }} className="mb-4" />
                <div className="overline mb-3" style={{ color: accent }}>Cover Coming Soon</div>
                <div className="font-serif text-xl text-[#F4F4F6] leading-tight">{book.title}</div>
              </div>
            ) : (
              <img
                src={book.cover_image}
                alt={book.title}
                onError={() => setImgFailed(true)}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                loading="lazy"
              />
            )}
            {!imgFailed && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            )}
            {book.featured && (
              <span
                className="absolute top-4 left-4 overline px-3 py-1 backdrop-blur-md rounded-full"
                style={{ backgroundColor: accentBg, color: accent, borderColor: accent }}
              >
                ✦ Featured
              </span>
            )}
          </div>
          <div className="p-7">
            {book.series && (
              <div className="overline mb-2" style={{ color: accent }}>
                {book.series}
                {book.series_order && ` · Book ${book.series_order}`}
              </div>
            )}
            <h3
              className="font-serif text-2xl text-[#F4F4F6] mb-3 leading-tight transition-colors"
              onMouseEnter={(e) => (e.currentTarget.style.color = accent)}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#F4F4F6")}
            >
              {book.title}
            </h3>
            <p className="text-sm text-[#A0A4AE] leading-relaxed mb-5 line-clamp-2">
              {book.tagline}
            </p>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-[#6C7280] tracking-widest uppercase">
                {book.release_date}
              </span>
              <span
                className="text-xs font-mono tracking-widest uppercase"
                style={{ color: accent }}
              >
                Read →
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
